import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tabs from '@mui/material/Tabs';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import BarChartIcon from '@mui/icons-material/BarChart';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GridOnIcon from '@mui/icons-material/GridOn';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import TornadoIcon from '@mui/icons-material/Tornado';
import TroubleshootIcon from '@mui/icons-material/Troubleshoot';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import HubIcon from '@mui/icons-material/Hub';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SpeedIcon from '@mui/icons-material/Speed';
import * as joint from '@joint/plus';
import {Chart as ChartJS} from 'chart.js';

import {CanvasEnvironment} from '../../../../utils/canvas/canvas-environment';
import {createFtGraphAndExpand} from '../../../../utils/canvas/canvas-actions';
import {FaultTreeNode} from '../../../../@types/faultTreeNode';
import {detectCCFGroups, MinimalCutSet, MinimalCutSetsResult,} from '../../../../utils/minimal-cut-sets';
import {escapeCSVValue, formatTimestamp} from '../../../../utils/export/unifiedReportExporter';
import {TabPanel} from '../../../form-components/TabPanel.component';
import CCFAnalysis from './CCFAnalysis.component';
import BaseEventFrequencyChart from './BaseEventFrequencyChart.component';
import ParetoChart from './ParetoChart.component';
import OrderDistributionChart from './OrderDistributionChart.component';
import CoOccurrenceHeatmap from './CoOccurrenceHeatmap.component';
import ImportanceMeasuresTable from './ImportanceMeasuresTable.component';
import CumulativeCoverageChart from './CumulativeCoverageChart.component';
import SensitivityExplorer from './SensitivityExplorer.component';
import FMEATable from './FMEATable.component';
import DiagnosticImportance from './DiagnosticImportance.component';
import TornadoChart from './TornadoChart.component';
import {exportReliabilityPdf} from '../../../../utils/export/reliabilityPdfExporter';
import InherentRiskGauge from './InherentRiskGauge.component';
import RiskMatrix from './RiskMatrix.component';
import {AdjustedRiskProbabilities, DEFAULT_RISK_WEIGHTS, RiskWeights} from '../../../../utils/inherent-risk-score';
import {computeSeveritySuggestion, SeveritySuggestion} from '../../../../utils/severity-suggestion';

// Blue highlighter for MCS row selection — distinct from hover highlight (red #ed2637)
const MCSHighlighterFrame = joint.dia.HighlighterView.extend({
    tagName: 'rect',
    attributes: {
        'stroke': '#1976d2',
        'stroke-width': 5,
        'fill': 'none',
        'pointer-events': 'none',
    },
    highlight(cellView: joint.dia.CellView) {
        const { padding = 0 } = this.options;
        const bbox = cellView.model.getBBox();
        bbox.x = bbox.y = 0;
        bbox.inflate(padding);
        this.vel.attr(bbox.toJSON());
    },
});

/**
 * Expands lazy-loaded ancestor paths so that deep base events become visible in the graph.
 * Returns true if a graph rebuild was triggered, false if all base events were already loaded.
 */
function expandPathsForBaseEvents(
    env: CanvasEnvironment,
    faultTree: { faultTreeNodes: FaultTreeNode[] },
    baseEventIds: Set<number>,
): boolean {
    // Check which base events are missing from the graph
    const missingIds: number[] = [];
    baseEventIds.forEach((id) => {
        if (!env.graph.getCell('ft-' + id)) {
            missingIds.push(id);
        }
    });
    if (missingIds.length === 0) return false;

    // Build parent map: nodeId -> parentId
    const parentMap = new Map<number, number>();
    for (const node of faultTree.faultTreeNodes) {
        if (node.parentId) {
            parentMap.set(node.id, node.parentId);
        }
    }

    // Get or create the expanded set for this event
    const eventId: number = env.activeTopNode!.get('data').id;
    if (!env.expandedLazyNodeIdsByEvent.has(eventId)) {
        env.expandedLazyNodeIdsByEvent.set(eventId, new Set());
    }
    const expandedSet = env.expandedLazyNodeIdsByEvent.get(eventId)!;

    // For each missing base event, walk up ancestors and add to expanded set
    let needsRebuild = false;
    for (const targetId of missingIds) {
        let currentId: number | undefined = parentMap.get(targetId);
        while (currentId !== undefined) {
            if (!expandedSet.has(currentId)) {
                expandedSet.add(currentId);
                needsRebuild = true;
            }
            currentId = parentMap.get(currentId);
        }
    }

    if (needsRebuild) {
        const viewportRect = env.paperScroller.getVisibleArea().inflate(50);
        createFtGraphAndExpand(env, env.activeTopNode!, env.graph, env.paper, env.paperScroller, viewportRect);
    }
    return needsRebuild;
}

interface MinimalCutSetsProps {
    open: boolean;
    onClose: () => void;
    env: CanvasEnvironment | null;
    collapsed: boolean;
    onToggleCollapse: () => void;
    resetKey: number;
    initialTab?: number;
}

type MCSSortField = 'index' | 'order' | 'probability' | 'contribution';
type SortDirection = 'asc' | 'desc';

function formatProbability(value: number): string {
    if (value === 0) return '0';
    if (value < 0.001) return value.toExponential(3);
    return value.toPrecision(4);
}

const TAB_OWNER_ID = 'mcs-panel';

const MinimalCutSetsPanel: React.FC<MinimalCutSetsProps> = ({ open, onClose, env, collapsed, onToggleCollapse, resetKey, initialTab }) => {
    const [result, setResult] = useState<MinimalCutSetsResult | null>(null);
    const [nodeNameMap, setNodeNameMap] = useState<Map<number, string>>(new Map());
    const [sortField, setSortField] = useState<MCSSortField>('index');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const highlightedIdsRef = useRef<Set<number>>(new Set());
    const highlightedLinkIdsRef = useRef<Set<string>>(new Set());
    const nodeMapRef = useRef<Map<number, FaultTreeNode>>(new Map());

    // Tab state
    const [activeTab, setActiveTab] = useState<number>(initialTab ?? 0);
    const [ccfGroupCount, setCcfGroupCount] = useState<number>(0);
    const ccfExportHandlerRef = useRef<(() => void) | null>(null);
    const [baseEventFilter, setBaseEventFilter] = useState<number[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const [showFrequencyChart, setShowFrequencyChart] = useState<boolean>(false);
    const [showParetoChart, setShowParetoChart] = useState<boolean>(false);
    const [showOrderChart, setShowOrderChart] = useState<boolean>(false);
    const [showCoOccurrence, setShowCoOccurrence] = useState<boolean>(false);
    const [showImportance, setShowImportance] = useState<boolean>(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [showCoverage, setShowCoverage] = useState<boolean>(false);
    const [showSensitivity, setShowSensitivity] = useState<boolean>(false);
    const [showFMEA, setShowFMEA] = useState<boolean>(false);
    const [showDiagnostic, setShowDiagnostic] = useState<boolean>(false);
    const [showTornado, setShowTornado] = useState<boolean>(false);
    const [showRiskGauge, setShowRiskGauge] = useState<boolean>(false);
    const [showRiskMatrix, setShowRiskMatrix] = useState<boolean>(false);
    const [riskMatrixSeverity, setRiskMatrixSeverity] = useState<number>(-1);
    const [riskMatrixOperationType, setRiskMatrixOperationType] = useState<'commercial' | 'general'>('commercial');
    const scrollTargetRef = useRef<string | null>(null);

    // Chart refs for PDF export
    const orderChartRef = useRef<ChartJS | null>(null);
    const frequencyChartRef = useRef<ChartJS | null>(null);
    const paretoChartRef = useRef<ChartJS | null>(null);
    const coverageChartRef = useRef<ChartJS | null>(null);
    const tornadoChartRef = useRef<ChartJS | null>(null);
    const diagnosticChartRef = useRef<ChartJS | null>(null);
    const riskGaugeChartRef = useRef<ChartJS | null>(null);

    const [sensitivityAdjustedProbs, setSensitivityAdjustedProbs] = useState<AdjustedRiskProbabilities | null>(null);
    const [riskWeights, setRiskWeights] = useState<RiskWeights>({...DEFAULT_RISK_WEIGHTS});

    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; cutSet: MinimalCutSet; index: number } | null>(null);

    /** Ref callback: scrolls element into view when it matches the pending scroll target, then clears it. */
    const scrollRef = useCallback((key: string) => (el: HTMLDivElement | null): void => {
        if (el && scrollTargetRef.current === key) {
            scrollTargetRef.current = null;
            requestAnimationFrame(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    }, []);

    // Sync activeTab when initialTab changes (e.g., menu selection while panel is already open)
    useEffect(() => {
        if (initialTab !== undefined) {
            setActiveTab(initialTab);
        }
    }, [initialTab]);

    const clearMCSHighlights = useCallback((): void => {
        if (!env) return;
        highlightedIdsRef.current.forEach((id) => {
            const cell = env.graph.getCell('ft-' + id);
            if (cell) {
                const view = cell.findView(env.paper);
                if (view) {
                    MCSHighlighterFrame.remove(view, 'mcs-frame');
                }
            }
        });
        highlightedIdsRef.current.clear();

        highlightedLinkIdsRef.current.forEach((linkId) => {
            const link = env.graph.getCell(linkId) as joint.dia.Link | null;
            if (link) {
                link.attr('line/stroke', '#000');
                link.attr('line/strokeWidth', 2);
            }
        });
        highlightedLinkIdsRef.current.clear();
    }, [env]);

    const handleRowClick = useCallback((index: number): void => {
        clearMCSHighlights();
        if (selectedIndex === index) {
            setSelectedIndex(null);
        } else {
            setSelectedIndex(index);
        }
    }, [selectedIndex, clearMCSHighlights]);

    const handleBaseEventClick = useCallback((baseEventId: number): void => {
        if (!env) return;
        clearMCSHighlights();

        const faultTree = env.activeTopNode?.get('data')?.faultTree;
        if (faultTree) {
            // Expand lazy-loaded ancestors if the node isn't in the graph yet
            expandPathsForBaseEvents(env, faultTree, new Set([baseEventId]));
        }

        // If the node exists but is hidden (inside a collapsed branch), expand predecessors
        // using the same approach as the Search Fault Tree feature
        const cell = env.graph.getCell('ft-' + baseEventId) as joint.dia.Element | null;
        if (cell && (cell.get('collapsed') || cell.get('hidden'))) {
            const predecessors = env.graph.getPredecessors(cell).reverse();
            for (const predecessor of predecessors) {
                (predecessor as joint.dia.Element).set('hidden', false);
                if (predecessor.get('collapsed') || predecessor.get('hasUnloadedChildren')) {
                    env.paper.trigger('element:expand', env.paper.findViewByModel(predecessor));
                }
            }
        }

        // Re-fetch cell after potential expansion and highlight it
        const finalCell = env.graph.getCell('ft-' + baseEventId) as joint.dia.Element | null;
        if (finalCell) {
            const view = finalCell.findView(env.paper);
            if (view) {
                MCSHighlighterFrame.add(view, 'body', 'mcs-frame', { padding: 5 });
                highlightedIdsRef.current.add(baseEventId);
            }
            env.paperScroller.centerElement(finalCell);
        }
    }, [env, clearMCSHighlights]);

    const handleLocateInFT = useCallback((cutSet: { baseEventIds: Set<number> }): void => {
        if (!env) return;
        clearMCSHighlights();

        // Expand lazy-loaded ancestor paths if any base events are beyond the load depth
        const faultTree = env.activeTopNode?.get('data')?.faultTree;
        if (faultTree) {
            expandPathsForBaseEvents(env, faultTree, cutSet.baseEventIds);
        }

        // Highlight base event nodes
        cutSet.baseEventIds.forEach((id) => {
            const cell = env.graph.getCell('ft-' + id);
            if (cell) {
                const view = cell.findView(env.paper);
                if (view) {
                    MCSHighlighterFrame.add(view, 'body', 'mcs-frame', { padding: 5 });
                    highlightedIdsRef.current.add(id);
                }
            }
        });

        // Highlight links from each base event up to the root node
        if (result) {
            const rootCellId = 'ft-' + result.rootNodeId;
            cutSet.baseEventIds.forEach((baseEventId) => {
                let current = env.graph.getCell('ft-' + baseEventId) as joint.dia.Element | null;
                while (current && String(current.id) !== rootCellId) {
                    let inbound = env.graph.getConnectedLinks(current, { inbound: true });

                    // Embedded barrier nodes have no inbound link — the link goes
                    // to their barrier container instead. Follow the container's
                    // inbound links to bridge the gap (same approach as drawDnaPath).
                    if (inbound.length === 0) {
                        const containerId = current.get('containerId') as string | undefined;
                        if (containerId) {
                            const container = env.graph.getCell(containerId) as joint.dia.Element | null;
                            if (container) {
                                inbound = env.graph.getConnectedLinks(container, { inbound: true });
                            }
                        }
                    }

                    if (inbound.length === 0) break;
                    const link = inbound[0];
                    const linkId = String(link.id);
                    if (!highlightedLinkIdsRef.current.has(linkId)) {
                        link.attr('line/stroke', '#1976d2');
                        link.attr('line/strokeWidth', 5);
                        highlightedLinkIdsRef.current.add(linkId);
                    }
                    current = link.getSourceElement();
                }
            });
        }

        // Scroll to the first highlighted base event node without changing zoom
        for (const id of highlightedIdsRef.current) {
            const cell = env.graph.getCell('ft-' + id) as joint.dia.Element | null;
            if (cell) {
                env.paperScroller.centerElement(cell);
                break;
            }
        }
    }, [env, clearMCSHighlights, result]);

    const handleContextMenu = useCallback((e: React.MouseEvent, index: number, cutSet: MinimalCutSet): void => {
        e.preventDefault();
        setSelectedIndex(index);
        setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, cutSet, index });
    }, []);

    // Clear highlights when panel closes or component unmounts
    useEffect(() => {
        if (!open) {
            clearMCSHighlights();
            setSelectedIndex(null);
        }
        return (): void => {
            clearMCSHighlights();
        };
    }, [open, clearMCSHighlights]);

    // Clear highlights when graph is rebuilt (FT view switch, FT reload)
    useEffect(() => {
        if (resetKey > 0) {
            clearMCSHighlights();
            setSelectedIndex(null);
        }
    }, [resetKey, clearMCSHighlights]);

    // Clear highlights when switching tabs
    const handleTabChange = useCallback((_event: React.SyntheticEvent, newValue: number): void => {
        clearMCSHighlights();
        setSelectedIndex(null);
        setActiveTab(newValue);
    }, [clearMCSHighlights]);

    const [computing, setComputing] = useState(false);

    useEffect(() => {
        if (open && env) {
            const selectedNode = env.selection[0];
            if (!selectedNode) return;

            const data = selectedNode.get('data');
            if (!data) return;

            // Get the fault tree from the active top node (the expanded FT view)
            const faultTree = env.activeTopNode?.get('data')?.faultTree;
            if (!faultTree) return;

            // Build and cache the nodeMap for CCF probability lookups
            const nMap = new Map<number, FaultTreeNode>();
            for (const node of faultTree.faultTreeNodes as FaultTreeNode[]) {
                nMap.set(node.id, node);
            }
            nodeMapRef.current = nMap;

            // Capture node name map at computation time so it survives view switches
            const nameMap = new Map<number, string>();
            for (const node of faultTree.faultTreeNodes as FaultTreeNode[]) {
                nameMap.set(node.id, node.uniqueId || node.name);
            }
            setNodeNameMap(nameMap);

            // Run BDD computation in a Web Worker to avoid blocking the UI
            setComputing(true);
            setResult(null);

            const startNodeId: number = data.id;

            // a new Worker is created each time the MCS panel open (open && env).
            // It posts the fault tree data (see worker.postMessage({ faultTree, startNodeId })), 
            // receives the result, then terminate itself (see worker.onmessage)
            const worker = new Worker(
                new URL('../../../../utils/robdd.worker.ts', import.meta.url),
            );

            worker.onmessage = (e: MessageEvent): void => {
                // Deserialize: arrays back to Sets
                const raw = e.data;
                const mcsResult: MinimalCutSetsResult = {
                    ...raw,
                    cutSets: raw.cutSets.map((cs: { baseEventIds: number[]; order: number; probability: number }) => ({
                        ...cs,
                        baseEventIds: new Set(cs.baseEventIds),
                    })),
                };

                setResult(mcsResult);
                setComputing(false);

                // Detect CCF groups so we know whether to show the CCF tab
                const groups = detectCCFGroups(mcsResult.cutSets, env.tagLinksByFtNodeId, env.tagById);
                setCcfGroupCount(groups.length);

                worker.terminate();
            };

            worker.postMessage({ faultTree, startNodeId });

            setSortField('index');
            setSortDirection('asc');
            setSelectedIndex(null);
            setBaseEventFilter([]);
            setSearchText('');

            return (): void => {
                // kill the worker if the dialog closes before it finishes
                worker.terminate();
            };
        }
    }, [open, env]);

    const handleSort = (field: MCSSortField): void => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection(field === 'probability' || field === 'contribution' ? 'desc' : 'asc');
        }
    };

    const baseEventOptions = useMemo((): Array<{ id: number; name: string }> => {
        if (!result) return [];
        const allIds = new Set<number>();
        result.cutSets.forEach((cs) => cs.baseEventIds.forEach((id) => allIds.add(id)));
        return Array.from(allIds)
            .map((id) => ({ id, name: nodeNameMap.get(id) || `ID:${id}` }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [result, nodeNameMap]);

    const totalProbability = useMemo((): number => {
        if (!result) return 0;
        return result.cutSets.reduce((sum, cs) => sum + cs.probability, 0);
    }, [result]);

    const severitySuggestion = useMemo((): SeveritySuggestion | null => {
        if (!env?.eventSequence?.events) return null;
        return computeSeveritySuggestion(env.eventSequence.events);
    }, [env?.eventSequence?.events]);

    const getBaseEventNamesText = useCallback((baseEventIds: Set<number>): string => {
        return Array.from(baseEventIds)
            .map((id) => nodeNameMap.get(id) || `ID:${id}`)
            .join(', ');
    }, [nodeNameMap]);

    /** Events appearing in ≥50% of cut sets — structural hubs. */
    const structuralHubIds = useMemo((): Set<number> => {
        if (!result || result.cutSets.length < 2) return new Set();
        const counts = new Map<number, number>();
        for (const cs of result.cutSets) {
            for (const id of cs.baseEventIds) {
                counts.set(id, (counts.get(id) || 0) + 1);
            }
        }
        const threshold = result.cutSets.length * 0.5;
        const hubs = new Set<number>();
        for (const [id, count] of counts) {
            if (count >= threshold) hubs.add(id);
        }
        return hubs;
    }, [result]);

    const renderBaseEventNames = useCallback((baseEventIds: Set<number>): React.ReactNode => {
        const ids = Array.from(baseEventIds);
        return ids.map((id, i) => {
            const name = nodeNameMap.get(id) || `ID:${id}`;
            const node = nodeMapRef.current.get(id);
            const isBarrier = node?.nodeClass === 'BARRIER';
            const isHub = structuralHubIds.has(id);
            return (
                <React.Fragment key={id}>
                    {i > 0 && ', '}
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e): void => { e.stopPropagation(); handleBaseEventClick(id); }}
                        onKeyDown={(e): void => { if (e.key === 'Enter') { e.stopPropagation(); handleBaseEventClick(id); } }}
                        style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: '2px',
                            ...(isBarrier ? { color: 'green' } : isHub ? { color: '#ed6c02' } : {}),
                        }}
                        title={`Locate "${name}" in fault tree`}
                    >
                        {name}
                    </span>
                </React.Fragment>
            );
        });
    }, [nodeNameMap, structuralHubIds, handleBaseEventClick]);

    const sortedCutSets = useMemo((): Array<{ index: number; cutSet: MinimalCutSet }> => {
        if (!result) return [];
        const indexed = result.cutSets.map((cutSet, i) => ({ index: i + 1, cutSet }));
        let filtered = baseEventFilter.length > 0
            ? indexed.filter((row) => baseEventFilter.every((id) => row.cutSet.baseEventIds.has(id)))
            : indexed;
        if (searchText.trim()) {
            const lower = searchText.trim().toLowerCase();
            filtered = filtered.filter((row) =>
                Array.from(row.cutSet.baseEventIds).some((id) => {
                    const node = nodeMapRef.current.get(id);
                    return (nodeNameMap.get(id) || '').toLowerCase().includes(lower)
                        || (node?.name || '').toLowerCase().includes(lower)
                        || (node?.uniqueId || '').toLowerCase().includes(lower);
                })
            );
        }
        const dir = sortDirection === 'asc' ? 1 : -1;
        filtered.sort((a, b) => {
            switch (sortField) {
                case 'index':
                    return (a.index - b.index) * dir;
                case 'order':
                    return (a.cutSet.order - b.cutSet.order) * dir;
                case 'probability':
                case 'contribution':
                    return (a.cutSet.probability - b.cutSet.probability) * dir;
                default:
                    return 0;
            }
        });
        return filtered;
    }, [result, sortField, sortDirection, baseEventFilter, searchText, nodeNameMap]);

    const handleExportCSV = useCallback((): void => {
        if (!result) return;

        // CCF tab: delegate to CCF component's export handler
        if (activeTab === 1 && ccfExportHandlerRef.current) {
            ccfExportHandlerRef.current();
            return;
        }

        // MCS tab: export MCS data
        const header = ['#', 'Order', 'Probability', 'Contribution %', 'Basic Events'].map(escapeCSVValue).join(',');
        const rows = sortedCutSets.map((row) =>
            [
                row.index,
                row.cutSet.order,
                row.cutSet.probability,
                totalProbability > 0 ? (row.cutSet.probability / totalProbability * 100).toFixed(1) : 0,
                escapeCSVValue(getBaseEventNamesText(row.cutSet.baseEventIds)),
            ].join(',')
        );

        const csv = [header, ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const rootName = result.rootNodeName.replace(/[^a-zA-Z0-9_-]/g, '_');
        link.setAttribute('href', url);
        link.setAttribute('download', `MCS_${rootName}_${formatTimestamp()}.csv`);
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [result, activeTab, sortedCutSets, getBaseEventNamesText]);

    const handleCcfExportReady = useCallback((handler: (() => void) | null): void => {
        ccfExportHandlerRef.current = handler;
    }, []);

    const handleCcfGroupCountChange = useCallback((count: number): void => {
        setCcfGroupCount(count);
    }, []);

    // ── PDF export dialog state ──
    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [pdfMcsTopN, setPdfMcsTopN] = useState(50);
    const [pdfIncludeMcs, setPdfIncludeMcs] = useState(true);
    const [pdfCharts, setPdfCharts] = useState({
        inherentRiskGauge: false,
        riskMatrix: false,
        orderDistribution: false,
        baseEventFrequency: false,
        pareto: false,
        cumulativeCoverage: false,
        tornado: false,
        diagnosticImportance: false,
    });
    const [pdfTables, setPdfTables] = useState({
        importanceMeasures: false,
        fmea: false,
        coOccurrence: false,
    });

    const handleOpenPdfDialog = useCallback((): void => {
        // Pre-check currently visible charts/tables
        setPdfCharts({
            inherentRiskGauge: showRiskGauge,
            riskMatrix: showRiskMatrix,
            orderDistribution: showOrderChart,
            baseEventFrequency: showFrequencyChart,
            pareto: showParetoChart,
            cumulativeCoverage: showCoverage,
            tornado: showTornado,
            diagnosticImportance: showDiagnostic,
        });
        setPdfTables({
            importanceMeasures: showImportance,
            fmea: showFMEA,
            coOccurrence: showCoOccurrence,
        });
        setPdfIncludeMcs(true);
        setPdfMcsTopN(result && result.cutSets.length > 50 ? 50 : result?.cutSets.length ?? 50);
        setPdfDialogOpen(true);
    }, [showRiskGauge, showRiskMatrix, showOrderChart, showFrequencyChart, showParetoChart, showCoverage, showTornado, showDiagnostic, showImportance, showFMEA, showCoOccurrence, result]);

    const handleExportPdf = useCallback((): void => {
        if (!result) return;
        setPdfDialogOpen(false);
        // Defer export by a frame so newly toggled charts have time to mount and populate refs
        requestAnimationFrame(() => {
            exportReliabilityPdf({
                rootNodeName: result.rootNodeName,
                cutSets: result.cutSets,
                totalProbability,
                totalBaseEvents: result.totalBaseEvents,
                computationTimeMs: result.computationTimeMs,
                truncated: result.truncated,
                nodeNameMap,
                nodeMap: nodeMapRef.current,
                structuralHubIds,
                chartRefs: {
                    orderDistribution: pdfCharts.orderDistribution ? orderChartRef.current : null,
                    baseEventFrequency: pdfCharts.baseEventFrequency ? frequencyChartRef.current : null,
                    pareto: pdfCharts.pareto ? paretoChartRef.current : null,
                    cumulativeCoverage: pdfCharts.cumulativeCoverage ? coverageChartRef.current : null,
                    tornado: pdfCharts.tornado ? tornadoChartRef.current : null,
                    diagnosticImportance: pdfCharts.diagnosticImportance ? diagnosticChartRef.current : null,
                    inherentRiskGauge: pdfCharts.inherentRiskGauge ? riskGaugeChartRef.current : null,
                },
                visibleTables: {
                    importanceMeasures: pdfTables.importanceMeasures,
                    fmea: pdfTables.fmea,
                    coOccurrence: pdfTables.coOccurrence,
                },
                mcsTopN: pdfIncludeMcs ? pdfMcsTopN : 0,
                riskWeights: riskWeights,
                showRiskMatrix: pdfCharts.riskMatrix,
                riskMatrixSeverity: riskMatrixSeverity,
                riskMatrixOperationType: riskMatrixOperationType,
            });
        });
    }, [result, totalProbability, nodeNameMap, structuralHubIds, pdfCharts, pdfTables, pdfIncludeMcs, pdfMcsTopN, riskWeights]);

    if (!open) return null;

    const showCcfTab = ccfGroupCount > 0 || activeTab === 1;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
            ...(fullscreen && {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                backgroundColor: 'background.paper',
            }),
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 0.5,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    minHeight: 40,
                    flexShrink: 0,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" component="div">
                        {activeTab === 1 ? 'Common Cause Failure Analysis' : 'Reliability Analysis'}
                    </Typography>
                    {result && activeTab === 0 && (
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            — {result.rootNodeName} ({result.cutSets.length} cut set{result.cutSets.length !== 1 ? 's' : ''})
                        </Typography>
                    )}
                    {result && activeTab === 1 && (
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            — {result.rootNodeName}
                        </Typography>
                    )}
                </Stack>
                <Stack direction="row" spacing={0.5}>
                    {result && activeTab === 0 && (
                        <>
                            {/* Group 1: Overview — What does the fault tree look like? */}
                            <Tooltip title={showOrderChart ? 'Hide order distribution' : 'Show order distribution'}>
                                <IconButton
                                    onClick={(): void => { if (!showOrderChart) scrollTargetRef.current = 'order'; setShowOrderChart(!showOrderChart); }}
                                    sx={{ color: 'inherit', bgcolor: showOrderChart ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <DonutSmallIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showFrequencyChart ? 'Hide frequency chart' : 'Show frequency chart'}>
                                <IconButton
                                    onClick={(): void => { if (!showFrequencyChart) scrollTargetRef.current = 'frequency'; setShowFrequencyChart(!showFrequencyChart); }}
                                    sx={{ color: 'inherit', bgcolor: showFrequencyChart ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <BarChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showRiskGauge ? 'Hide structural vulnerability' : 'Show structural vulnerability'}>
                                <IconButton
                                    onClick={(): void => { if (!showRiskGauge) scrollTargetRef.current = 'riskGauge'; setShowRiskGauge(!showRiskGauge); }}
                                    sx={{ color: 'inherit', bgcolor: showRiskGauge ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <SpeedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                            {/* Group 2: Probability — Where is the risk? */}
                            <Tooltip title={showParetoChart ? 'Hide Pareto chart' : 'Show Pareto chart'}>
                                <IconButton
                                    onClick={(): void => { if (!showParetoChart) scrollTargetRef.current = 'pareto'; setShowParetoChart(!showParetoChart); }}
                                    sx={{ color: 'inherit', bgcolor: showParetoChart ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <ShowChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showCoverage ? 'Hide coverage by order' : 'Show coverage by order'}>
                                <IconButton
                                    onClick={(): void => { if (!showCoverage) scrollTargetRef.current = 'coverage'; setShowCoverage(!showCoverage); }}
                                    sx={{ color: 'inherit', bgcolor: showCoverage ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <StackedLineChartIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showImportance ? 'Hide risk significance' : 'Show risk significance'}>
                                <IconButton
                                    onClick={(): void => { if (!showImportance) scrollTargetRef.current = 'importance'; setShowImportance(!showImportance); }}
                                    sx={{ color: 'inherit', bgcolor: showImportance ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <AssessmentIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showTornado ? 'Hide tornado chart' : 'Show tornado chart'}>
                                <IconButton
                                    onClick={(): void => { if (!showTornado) scrollTargetRef.current = 'tornado'; setShowTornado(!showTornado); }}
                                    sx={{ color: 'inherit', bgcolor: showTornado ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <TornadoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showSensitivity ? 'Hide what-if explorer' : 'Show what-if explorer'}>
                                <IconButton
                                    onClick={(): void => { if (!showSensitivity) { scrollTargetRef.current = 'sensitivity'; } else { setSensitivityAdjustedProbs(null); } setShowSensitivity(!showSensitivity); }}
                                    sx={{ color: 'inherit', bgcolor: showSensitivity ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <TuneIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showFMEA ? 'Hide Criticality Analysis table' : 'Show Criticality Analysis table'}>
                                <IconButton
                                    onClick={(): void => { if (!showFMEA) scrollTargetRef.current = 'fmea'; setShowFMEA(!showFMEA); }}
                                    sx={{ color: 'inherit', bgcolor: showFMEA ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <FactCheckIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                            {/* Group 3: Relationships — How do events interact? */}
                            <Tooltip title={showCoOccurrence ? 'Hide double failure matrix' : 'Show double failure matrix'}>
                                <IconButton
                                    onClick={(): void => { if (!showCoOccurrence) scrollTargetRef.current = 'cooccurrence'; setShowCoOccurrence(!showCoOccurrence); }}
                                    sx={{ color: 'inherit', bgcolor: showCoOccurrence ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <GridOnIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={showDiagnostic ? 'Hide failure attribution' : 'Show failure attribution'}>
                                <IconButton
                                    onClick={(): void => { if (!showDiagnostic) scrollTargetRef.current = 'diagnostic'; setShowDiagnostic(!showDiagnostic); }}
                                    sx={{ color: 'inherit', bgcolor: showDiagnostic ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <TroubleshootIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                            <Tooltip title={showRiskMatrix ? 'Hide risk matrix' : 'Show risk matrix'}>
                                <IconButton
                                    onClick={(): void => { if (!showRiskMatrix) scrollTargetRef.current = 'riskMatrix'; setShowRiskMatrix(!showRiskMatrix); }}
                                    sx={{ color: 'inherit', bgcolor: showRiskMatrix ? 'rgba(255,255,255,0.2)' : 'transparent', borderRadius: 1 }}
                                    size="small"
                                >
                                    <WarningAmberIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.3)' }} />
                            {(showRiskGauge || showOrderChart || showFrequencyChart || showParetoChart || showCoverage || showImportance || showSensitivity || showTornado || showFMEA || showCoOccurrence || showDiagnostic) && (
                                <Tooltip title="Collapse all charts and tables">
                                    <IconButton
                                        onClick={(): void => {
                                            setShowOrderChart(false);
                                            setShowFrequencyChart(false);
                                            setShowParetoChart(false);
                                            setShowCoverage(false);
                                            setShowImportance(false);
                                            setShowSensitivity(false);
                                            setSensitivityAdjustedProbs(null);
                                            setShowTornado(false);
                                            setShowFMEA(false);
                                            setShowCoOccurrence(false);
                                            setShowDiagnostic(false);
                                            setShowRiskGauge(false);
                                            setShowRiskMatrix(false);
                                        }}
                                        sx={{ color: 'inherit' }}
                                        size="small"
                                    >
                                        <VisibilityOffIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </>
                    )}
                    {result && activeTab === 0 && (
                        <Tooltip title="Export to PDF">
                            <IconButton
                                onClick={handleOpenPdfDialog}
                                sx={{ color: 'inherit' }}
                                size="small"
                            >
                                <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={fullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
                        <IconButton
                            onClick={(): void => setFullscreen(!fullscreen)}
                            sx={{ color: 'inherit' }}
                            size="small"
                        >
                            {fullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    {!fullscreen && (
                        <Tooltip title={collapsed ? 'Expand panel' : 'Collapse panel'}>
                            <IconButton
                                onClick={onToggleCollapse}
                                sx={{ color: 'inherit' }}
                                size="small"
                            >
                                {collapsed ? <UnfoldMoreIcon fontSize="small" /> : <UnfoldLessIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        onClick={onClose}
                        sx={{ color: 'inherit' }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
            </Box>

            {/* Body — hidden when collapsed */}
            {!collapsed && (
                <>
                    {/* Tab bar — only show when CCF tab is available */}
                    {showCcfTab && (
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                minHeight: 36,
                                borderBottom: 1,
                                borderColor: 'divider',
                                flexShrink: 0,
                                '& .MuiTab-root': {
                                    minHeight: 36,
                                    textTransform: 'none',
                                    fontSize: '0.8125rem',
                                    py: 0,
                                },
                            }}
                        >
                            <Tab
                                label="MCS"
                                id={`${TAB_OWNER_ID}-tab-0`}
                                aria-controls={`${TAB_OWNER_ID}-tab-0-tabpanel`}
                            />
                            <Tab
                                label="CCF"
                                id={`${TAB_OWNER_ID}-tab-1`}
                                aria-controls={`${TAB_OWNER_ID}-tab-1-tabpanel`}
                            />
                        </Tabs>
                    )}

                    {/* MCS Tab */}
                    <TabPanel value={activeTab} index={0} ownerId={`${TAB_OWNER_ID}-tab-0`}>

                        {/* Group 1: Overview */}
                        {result && showOrderChart && (
                            <div ref={scrollRef('order')}>
                                <OrderDistributionChart cutSets={result.cutSets} chartRef={orderChartRef} />
                            </div>
                        )}

                        {result && showFrequencyChart && (
                            <div ref={scrollRef('frequency')}>
                                <BaseEventFrequencyChart
                                    cutSets={result.cutSets}
                                    nodeNameMap={nodeNameMap}
                                    nodeMap={nodeMapRef.current}
                                    structuralHubIds={structuralHubIds}
                                    chartRef={frequencyChartRef}
                                    onBarClick={(id): void => {
                                        setBaseEventFilter((prev) =>
                                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                        );
                                    }}
                                />
                            </div>
                        )}

                        {/* Group 2: Probability */}
                        {result && showRiskGauge && (
                            <div ref={scrollRef('riskGauge')}>
                                <InherentRiskGauge
                                    cutSets={result.cutSets}
                                    totalProbability={totalProbability}
                                    totalBaseEvents={result.totalBaseEvents}
                                    structuralHubIds={structuralHubIds}
                                    chartRef={riskGaugeChartRef}
                                    adjustedProbabilities={sensitivityAdjustedProbs}
                                    onWeightsChange={setRiskWeights}
                                    topNodeUniqueId={nodeMapRef.current.get(result.rootNodeId)?.uniqueId}
                                    topNodeName={nodeMapRef.current.get(result.rootNodeId)?.name}
                                />
                            </div>
                        )}
                        {result && showRiskMatrix && (
                            <div ref={scrollRef('riskMatrix')}>
                                <RiskMatrix
                                    totalProbability={totalProbability}
                                    topNodeUniqueId={nodeMapRef.current.get(result.rootNodeId)?.uniqueId}
                                    topNodeName={nodeMapRef.current.get(result.rootNodeId)?.name}
                                    onSeverityChange={setRiskMatrixSeverity}
                                    onOperationTypeChange={setRiskMatrixOperationType}
                                    suggestedSeverity={severitySuggestion?.suggestedSeverity}
                                    suggestedLikelihood={severitySuggestion?.suggestedLikelihood}
                                    endNodeDetails={severitySuggestion?.endNodeDetails}
                                />
                            </div>
                        )}

                        {result && showParetoChart && (
                            <div ref={scrollRef('pareto')}>
                                <ParetoChart
                                    cutSets={result.cutSets}
                                    totalProbability={totalProbability}
                                    chartRef={paretoChartRef}
                                />
                            </div>
                        )}

                        {result && showCoverage && (
                            <div ref={scrollRef('coverage')}>
                                <CumulativeCoverageChart
                                    cutSets={result.cutSets}
                                    totalProbability={totalProbability}
                                    chartRef={coverageChartRef}
                                />
                            </div>
                        )}

                        {result && showImportance && (
                            <div ref={scrollRef('importance')}>
                                <ImportanceMeasuresTable
                                    cutSets={result.cutSets}
                                    nodeMap={nodeMapRef.current}
                                    nodeNameMap={nodeNameMap}
                                    totalProbability={totalProbability}
                                    structuralHubIds={structuralHubIds}
                                    onEventClick={(id): void => {
                                        setBaseEventFilter((prev) =>
                                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                        );
                                    }}
                                />
                            </div>
                        )}

                        {result && showTornado && (
                            <div ref={scrollRef('tornado')}>
                                <TornadoChart
                                    cutSets={result.cutSets}
                                    nodeMap={nodeMapRef.current}
                                    nodeNameMap={nodeNameMap}
                                    totalProbability={totalProbability}
                                    structuralHubIds={structuralHubIds}
                                    chartRef={tornadoChartRef}
                                    onBarClick={(id): void => {
                                        setBaseEventFilter((prev) =>
                                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                        );
                                    }}
                                />
                            </div>
                        )}

                        {result && showSensitivity && (
                            <div ref={scrollRef('sensitivity')}>
                                <SensitivityExplorer
                                    cutSets={result.cutSets}
                                    nodeMap={nodeMapRef.current}
                                    nodeNameMap={nodeNameMap}
                                    totalProbability={totalProbability}
                                    structuralHubIds={structuralHubIds}
                                    onAdjustedProbabilityChange={setSensitivityAdjustedProbs}
                                />
                            </div>
                        )}

                        {result && showFMEA && (
                            <div ref={scrollRef('fmea')}>
                                <FMEATable
                                    cutSets={result.cutSets}
                                    nodeMap={nodeMapRef.current}
                                    nodeNameMap={nodeNameMap}
                                    totalProbability={totalProbability}
                                    structuralHubIds={structuralHubIds}
                                />
                            </div>
                        )}

                        {/* Group 3: Relationships */}
                        {result && showCoOccurrence && (
                            <div ref={scrollRef('cooccurrence')}>
                                <CoOccurrenceHeatmap
                                    cutSets={result.cutSets}
                                    nodeNameMap={nodeNameMap}
                                />
                            </div>
                        )}

                        {result && showDiagnostic && (
                            <div ref={scrollRef('diagnostic')}>
                                <DiagnosticImportance
                                    cutSets={result.cutSets}
                                    nodeMap={nodeMapRef.current}
                                    nodeNameMap={nodeNameMap}
                                    totalProbability={totalProbability}
                                    structuralHubIds={structuralHubIds}
                                    chartRef={diagnosticChartRef}
                                    onBarClick={(id): void => {
                                        setBaseEventFilter((prev) =>
                                            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                                        );
                                    }}
                                />
                            </div>
                        )}

                        {/* Summary */}
                        {result && (
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, pt: 1, pb: 0.5 }}>
                                <Typography variant="subtitle2">
                                        Minimal Cut Sets
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                            ({sortedCutSets.length === result.cutSets.length
                                            ? `${result.cutSets.length}`
                                            : `${sortedCutSets.length} / ${result.cutSets.length}`})
                                    </Typography>
                                </Typography>
                                <Tooltip title="Export to CSV">
                                    <IconButton size="small" onClick={handleExportCSV}>
                                        <FileDownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        )}
                        {result && (
                            <Box sx={{ px: 2, py: 1, borderBottom: 1, orderTop: 1, borderColor: 'divider' }}>
                                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" sx={{ flexShrink: 0 }}>
                                        {baseEventFilter.length > 0
                                            ? `${sortedCutSets.length} of ${result.cutSets.length} cut set${result.cutSets.length !== 1 ? 's' : ''}`
                                            : `${result.cutSets.length} cut set${result.cutSets.length !== 1 ? 's' : ''} found`}
                                        {' '}({result.totalBaseEvents} basic event{result.totalBaseEvents !== 1 ? 's' : ''},
                                            computed in {result.computationTimeMs.toFixed(1)}ms)
                                        {result.truncated && ' (truncated)'}
                                    </Typography>
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={baseEventOptions}
                                        getOptionLabel={(option): string => option.name}
                                        isOptionEqualToValue={(option, value): boolean => option.id === value.id}
                                        value={baseEventOptions.filter((o) => baseEventFilter.includes(o.id))}
                                        onChange={(_e, values): void => setBaseEventFilter(values.map((v) => v.id))}
                                        renderTags={(value, getTagProps): React.ReactNode =>
                                            value.map((option, index) => {
                                                const { key, ...rest } = getTagProps({ index });
                                                return <Chip key={key} label={option.name} size="small" {...rest} />;
                                            })
                                        }
                                        renderInput={(params): React.ReactElement => (
                                            <TextField {...params} placeholder="Filter MCS table by basic event" variant="outlined" />
                                        )}
                                        sx={{ minWidth: 250, flex: 1 }}
                                    />
                                    {/*
                                        <TextField
                                            size="small"
                                            placeholder="Search MCS table by basic events"
                                            value={searchText}
                                            onChange={(e): void => setSearchText(e.target.value)}
                                            slotProps={{
                                                input: {
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                            sx={{ minWidth: 180 }}
                                        />
                                        */}
                                </Stack>
                                {result.truncated && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                            Results truncated to 1000 cut sets. The full tree may have more.
                                    </Alert>
                                )}
                                {((): React.ReactElement | null => {
                                    const spfCount = result.cutSets.filter((cs) => cs.baseEventIds.size === 1).length;
                                    const hubCount = structuralHubIds.size;
                                    const parts: string[] = [];
                                    if (spfCount > 0) parts.push(`${spfCount} single point${spfCount !== 1 ? 's' : ''} of failure`);
                                    if (hubCount > 0) parts.push(`${hubCount} structural hub${hubCount !== 1 ? 's' : ''}`);
                                    if (parts.length === 0) return null;
                                    return (
                                        <Alert severity={spfCount > 0 ? 'warning' : 'info'} sx={{ mt: 1 }}>
                                            {parts.join(', ')}
                                        </Alert>
                                    );
                                })()}
                            </Box>
                        )}


                        {/* Legend */}
                        {result && (
                            <Stack direction="row" spacing={2} sx={{ px: 2, py: 0.5 }}>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                    <Typography variant="caption" color="text.secondary">Single point of failure</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                                    <Typography variant="caption" color="text.secondary">Structural hub (appears in ≥50% of cut sets)</Typography>
                                </Stack>
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'green' }} />
                                    <Typography variant="caption" color="text.secondary">Barrier</Typography>
                                </Stack>
                            </Stack>
                        )}

                        {/* MCS Table */}
                        {result && (
                            <TableContainer component={Paper} elevation={0} sx={{ overflow: 'visible' }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'index'}
                                                    direction={sortField === 'index' ? sortDirection : 'asc'}
                                                    onClick={(): void => handleSort('index')}
                                                >
                                                            #
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'order'}
                                                    direction={sortField === 'order' ? sortDirection : 'asc'}
                                                    onClick={(): void => handleSort('order')}
                                                >
                                                            Order
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'probability'}
                                                    direction={sortField === 'probability' ? sortDirection : 'desc'}
                                                    onClick={(): void => handleSort('probability')}
                                                >
                                                            Probability
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'contribution'}
                                                    direction={sortField === 'contribution' ? sortDirection : 'desc'}
                                                    onClick={(): void => handleSort('contribution')}
                                                >
                                                            Contribution
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Basic Events</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedCutSets.map((row) => (
                                            <TableRow
                                                key={row.index}
                                                hover
                                                selected={selectedIndex === row.index}
                                                onClick={(): void => handleRowClick(row.index)}
                                                onContextMenu={(e): void => handleContextMenu(e, row.index, row.cutSet)}
                                                sx={{ cursor: 'pointer', '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                            >
                                                <TableCell>
                                                    {((): React.ReactElement => {
                                                        const isSPF = row.cutSet.order === 1;
                                                        const hasHub = Array.from(row.cutSet.baseEventIds).some((id) => structuralHubIds.has(id));
                                                        if (!isSPF && !hasHub) return <>{row.index}</>;
                                                        return (
                                                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                                                {isSPF && (
                                                                    <Tooltip title="Single point of failure — no redundancy protects against this event" arrow>
                                                                        <LooksOneIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                                                    </Tooltip>
                                                                )}
                                                                {hasHub && (
                                                                    <Tooltip title="Contains a structural hub — event appearing in ≥50% of cut sets" arrow>
                                                                        <HubIcon sx={{ fontSize: 14, color: '#ed6c02' }} />
                                                                    </Tooltip>
                                                                )}
                                                                <span>{row.index}</span>
                                                            </Stack>
                                                        );
                                                    })()}
                                                </TableCell>
                                                <TableCell>{row.cutSet.order}</TableCell>
                                                <TableCell>{formatProbability(row.cutSet.probability)}</TableCell>
                                                <TableCell>{totalProbability > 0 ? `${(row.cutSet.probability / totalProbability * 100).toFixed(1)}%` : '—'}</TableCell>
                                                <TableCell>{renderBaseEventNames(row.cutSet.baseEventIds)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <Menu
                            open={contextMenu !== null}
                            onClose={(): void => setContextMenu(null)}
                            anchorReference="anchorPosition"
                            anchorPosition={
                                contextMenu !== null
                                    ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                                    : undefined
                            }
                        >
                            <MenuItem
                                onClick={(): void => {
                                    if (contextMenu) {
                                        handleLocateInFT(contextMenu.cutSet);
                                        setSelectedIndex(contextMenu.index);
                                    }
                                    setContextMenu(null);
                                }}
                            >
                                <ListItemIcon>
                                    <MyLocationIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Locate in Fault Tree</ListItemText>
                            </MenuItem>
                            <MenuItem
                                onClick={(): void => {
                                    if (contextMenu) {
                                        const text = getBaseEventNamesText(contextMenu.cutSet.baseEventIds);
                                        navigator.clipboard.writeText(text);
                                    }
                                    setContextMenu(null);
                                }}
                            >
                                <ListItemIcon>
                                    <ContentCopyIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText>Copy Basic Events</ListItemText>
                            </MenuItem>
                        </Menu>
                        {!result && (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {computing
                                        ? 'Computing minimal cut sets\u2026'
                                        : 'No fault tree data available. Expand a fault tree first.'}
                                </Typography>
                            </Box>
                        )}
                    </TabPanel>

                    {/* CCF Tab — always mounted so it can report group count; hidden by TabPanel when not active */}
                    {result && env && (
                        <TabPanel value={activeTab} index={1} ownerId={`${TAB_OWNER_ID}-tab-1`}>
                            {ccfGroupCount > 0 ? (
                                <CCFAnalysis
                                    result={result}
                                    nodeNameMap={nodeNameMap}
                                    nodeMap={nodeMapRef.current}
                                    env={env}
                                    onRowClick={handleRowClick}
                                    selectedIndex={selectedIndex}
                                    onExportReady={handleCcfExportReady}
                                    onGroupCountChange={handleCcfGroupCountChange}
                                />
                            ) : (
                                <Alert severity="info" sx={{ m: 2 }}>
                                    No common cause failure groups detected. To enable CCF analysis,
                                    tag two or more basic events with a shared tag representing a
                                    common cause (e.g., same manufacturer, shared environment, common
                                    maintenance procedure).
                                </Alert>
                            )}
                        </TabPanel>
                    )}
                </>
            )}

            {/* PDF Export Options Dialog */}
            <Dialog open={pdfDialogOpen} onClose={(): void => setPdfDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>Export to PDF</DialogTitle>
                <DialogContent>
                    {/* MCS Table */}
                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Cut Sets Table</Typography>
                    <FormControlLabel
                        control={<Checkbox checked={pdfIncludeMcs} onChange={(e): void => setPdfIncludeMcs(e.target.checked)} size="small" />}
                        label="Include MCS table"
                    />
                    {pdfIncludeMcs && result && result.cutSets.length > 20 && (
                        <TextField
                            select
                            size="small"
                            label="Top cut sets by contribution"
                            value={pdfMcsTopN}
                            onChange={(e): void => setPdfMcsTopN(Number(e.target.value))}
                            sx={{ ml: 4, mt: 0.5, minWidth: 200 }}
                        >
                            {[20, 50, 100, 200].filter((n) => n < result.cutSets.length).map((n) => (
                                <MenuItem key={n} value={n}>Top {n}</MenuItem>
                            ))}
                            <MenuItem value={result.cutSets.length}>All ({result.cutSets.length})</MenuItem>
                        </TextField>
                    )}

                    {/* Charts — checking a chart auto-enables it in the panel so its canvas ref is available */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Charts</Typography>
                    <Stack>
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.inherentRiskGauge} onChange={(e): void => { setPdfCharts((p) => ({ ...p, inherentRiskGauge: e.target.checked })); if (e.target.checked) setShowRiskGauge(true); }} size="small" />}
                            label="Structural Vulnerability"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.riskMatrix} onChange={(e): void => { setPdfCharts((p) => ({ ...p, riskMatrix: e.target.checked })); if (e.target.checked) setShowRiskMatrix(true); }} size="small" />}
                            label="Risk Matrix (FAA Order 8040.4C)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.orderDistribution} onChange={(e): void => { setPdfCharts((p) => ({ ...p, orderDistribution: e.target.checked })); if (e.target.checked) setShowOrderChart(true); }} size="small" />}
                            label="Order Distribution"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.baseEventFrequency} onChange={(e): void => { setPdfCharts((p) => ({ ...p, baseEventFrequency: e.target.checked })); if (e.target.checked) setShowFrequencyChart(true); }} size="small" />}
                            label="Base Event Frequency"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.pareto} onChange={(e): void => { setPdfCharts((p) => ({ ...p, pareto: e.target.checked })); if (e.target.checked) setShowParetoChart(true); }} size="small" />}
                            label="Pareto Analysis"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.cumulativeCoverage} onChange={(e): void => { setPdfCharts((p) => ({ ...p, cumulativeCoverage: e.target.checked })); if (e.target.checked) setShowCoverage(true); }} size="small" />}
                            label="Cumulative Coverage"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.tornado} onChange={(e): void => { setPdfCharts((p) => ({ ...p, tornado: e.target.checked })); if (e.target.checked) setShowTornado(true); }} size="small" />}
                            label="Tornado Chart"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfCharts.diagnosticImportance} onChange={(e): void => { setPdfCharts((p) => ({ ...p, diagnosticImportance: e.target.checked })); if (e.target.checked) setShowDiagnostic(true); }} size="small" />}
                            label="Failure Attribution"
                        />
                    </Stack>

                    {/* Tables */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Tables</Typography>
                    <Stack>
                        <FormControlLabel
                            control={<Checkbox checked={pdfTables.importanceMeasures} onChange={(e): void => setPdfTables((p) => ({ ...p, importanceMeasures: e.target.checked }))} size="small" />}
                            label="Risk Significance (RC / RIF / RRF)"
                        />
                        <FormControlLabel
                            control={<Checkbox checked={pdfTables.fmea} onChange={(e): void => setPdfTables((p) => ({ ...p, fmea: e.target.checked }))} size="small" />}
                            label="Criticality Analysis (S / O / D / RPN)"
                        />
                        {result && ((): boolean => {
                            const coIds = new Set<number>();
                            result.cutSets.forEach((cs) => { if (cs.order >= 2) cs.baseEventIds.forEach((id) => coIds.add(id)); });
                            return coIds.size > 0 && coIds.size <= 50;
                        })() && (
                            <FormControlLabel
                                control={<Checkbox checked={pdfTables.coOccurrence} onChange={(e): void => setPdfTables((p) => ({ ...p, coOccurrence: e.target.checked }))} size="small" />}
                                label="Double Failure Matrix"
                            />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={(): void => setPdfDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleExportPdf}
                        disabled={!pdfIncludeMcs && !Object.values(pdfCharts).some(Boolean) && !Object.values(pdfTables).some(Boolean)}
                    >
                        Export
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MinimalCutSetsPanel;
